import type { QueryClient } from "@tanstack/react-query";
import type { VideoConfig } from "./video-embeds";
import { siteContextQO, videoFromOverride, applyTitleSuffix, type SiteContext } from "./site-context";
import { buildAliasHead } from "./alias-routes";
import { withTodayDate } from "./dateIntent";

type AliasMeta = { title: string; description: string; path: string; faqs?: { q: string; a: string }[]; utilityId?: string };

export type AliasLoaderData = { video?: VideoConfig; titleSuffix: string };

export async function loadAliasContext(queryClient: QueryClient, slug: string, fallback?: VideoConfig): Promise<AliasLoaderData> {
  const ctx = (await queryClient.ensureQueryData(siteContextQO)) as SiteContext;
  return {
    video: videoFromOverride(ctx.videos[slug], fallback),
    titleSuffix: ctx.settings.title_suffix ?? "",
  };
}

export function aliasFallbackData(fallback?: VideoConfig): AliasLoaderData {
  return { video: fallback, titleSuffix: "" };
}

export function makeAliasLoader(slug: string, fallback?: VideoConfig) {
  return ({ context }: { context: { queryClient: QueryClient } }) => {
    void context.queryClient.prefetchQuery(siteContextQO);
    return aliasFallbackData(fallback);
  };
}

export function makeAliasHead(meta: AliasMeta, fallback?: VideoConfig) {
  return ({ loaderData }: { loaderData?: AliasLoaderData }) =>
    buildAliasHead({
      title: withTodayDate(applyTitleSuffix(meta.title, loaderData?.titleSuffix)),
      description: meta.description,
      path: meta.path,
      video: loaderData?.video ?? fallback,
      faqs: meta.faqs,
      utilityId: meta.utilityId,
    });
}